<template>
    <div>
        <h2
            class="text-xl md:text-2xl font-bold text-gray-500 text-center my-8"
        >
            슬라이드 관리
        </h2>

        <div class="max-w-3xl mx-auto mt-8">
            <div class="bg-white shadow overflow-hidden sm:rounded-md mb-8">
                <ul>
                    <li
                        v-for="(slide, i) in slides"
                        :key="slide.id"
                        :class="{ 'border-t border-gray-200': i > 0 }"
                    >
                        <div
                            class="hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out"
                        >
                            <div class="flex items-center px-4 py-4 sm:px-6">
                                <div class="min-w-0 flex-1 flex items-center">
                                    <div class="flex-shrink-0">
                                        <img
                                            class="h-12 w-24 rounded"
                                            :src="slide.image"
                                            alt=""
                                        />
                                    </div>
                                    <div
                                        class="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4"
                                    >
                                        <div>
                                            <div
                                                class="text-sm leading-5 font-medium text-indigo-600 truncate"
                                            >
                                                {{ slide.excerpt }}
                                            </div>
                                            <div
                                                class="mt-2 flex items-center text-sm leading-5 text-gray-500"
                                            >
                                                <span class="truncate">{{
                                                    slide.link
                                                }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex items-center text-gray-400">
                                    <button
                                        type="button"
                                        @click="editSlide(slide)"
                                        class="hover:text-gray-800"
                                    >
                                        <svg
                                            class="h-5 w-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <title>수정</title>
                                            <path
                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                            ></path>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        @click="deleteSlide(slide)"
                                        class="ml-1 hover:text-gray-800"
                                    >
                                        <svg
                                            class="h-5 w-5"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            viewBox="0 0 24 24"
                                        >
                                            <title>삭제</title>
                                            <path d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>

            <form v-if="mode" class="mt-20">
                <hr class="max-w-md mx-auto mb-12" />
                <div>
                    <input
                        id="image"
                        type="text"
                        placeholder="이미지 URL (https://)"
                        v-model.trim="$v.slide.image.$model"
                        class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                    />
                    <div
                        v-if="$v.slide.image.$error || errors.image"
                        class="text-red-500 text-xs italic"
                    >
                        필수입력 항목입니다.
                    </div>
                </div>
                <div class="mt-2">
                    <input
                        id="link"
                        type="text"
                        placeholder="링크"
                        v-model="slide.link"
                        class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                    />
                </div>
                <div class="mt-2">
                    <textarea
                        id="body"
                        rows="8"
                        placeholder="내용"
                        v-model="slide.body"
                        class="my-1 form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                    ></textarea>
                </div>
                <div
                    class="grid grid-cols-1 row-gap-6 col-gap-3 sm:grid-cols-2"
                >
                    <div>
                        <input
                            id="color"
                            type="text"
                            placeholder="오버레이"
                            v-model="slide.color"
                            class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        />
                    </div>
                    <div>
                        <input
                            id="position"
                            type="text"
                            placeholder="포지션"
                            v-model="slide.position"
                            class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        />
                    </div>
                </div>
                <div class="flex items-center mt-3">
                    <input
                        id="active"
                        type="checkbox"
                        class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                        v-model="slide.active"
                    />
                    <label
                        for="active"
                        class="ml-2 block text-sm leading-5 text-gray-900"
                    >
                        활성
                    </label>
                </div>
                <div class="mt-6">
                    <span class="inline-flex rounded-md shadow-sm">
                        <button
                            type="button"
                            @click="saveSlide"
                            class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                            :disabled="$v.$invalid || saving"
                            :class="{
                                'opacity-50 cursor-not-allowed':
                                    $v.$invalid || saving,
                            }"
                        >
                            {{ buttonText }}
                        </button>
                    </span>
                    <button
                        type="button"
                        @click="cancel"
                        class="py-2 px-4 text-sm rounded-md focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo"
                    >
                        취소
                    </button>
                </div>
            </form>

            <div v-if="mode === null" class="mt-16">
                <span class="inline-flex rounded-md shadow-sm">
                    <button
                        type="button"
                        @click="mode = 'new'"
                        class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                    >
                        슬라이드 등록
                    </button>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

export default {
    mixins: [validationMixin],

    data() {
        return {
            mode: null,
            saving: false,
            errors: [],
            slides: [],
            slide: {
                image: null,
            },
        }
    },

    validations() {
        return {
            slide: {
                image: {
                    required,
                },
            },
        }
    },

    computed: {
        // activeSlides() {
        //   return this.slides.filter(x => x.active === true)
        // },
        buttonText() {
            return this.mode === 'new' ? '등록' : '수정'
        },
    },

    created() {
        this.fetchData()
    },

    methods: {
        async fetchData() {
            try {
                const response = await axios.get('/api/slides')

                this.slides = response.data.data
            } catch (error) {
                console.error(error)
            }
        },

        editSlide(slide) {
            this.mode = 'edit'
            this.slide = Object.assign({}, slide)
        },

        async saveSlide() {
            this.saving = true

            const method = this.mode === 'new' ? 'post' : 'patch'
            let endpoint = '/admin/slides'
            if (this.mode === 'edit') {
                endpoint += `/${this.slide.id}`
            }

            try {
                const response = await axios[method](endpoint, {
                    image: this.slide.image,
                    link: this.slide.link,
                    active: this.slide.active,
                    body: this.slide.body,
                    color: this.slide.color,
                    position: this.slide.position,
                })

                this.saving = false
                this.fetchData()
                // const slide = response.data.data

                // if (this.mode === 'new') {
                //   this.slides.push(slide)
                // } else {
                //   const index = this.slides.findIndex(x => x.id === slide.id)
                //   this.slides[index] = Object.assign({}, slide)
                // }

                this.cancel()
                this.toast(`슬라이더를 ${this.buttonText}했습니다`)
            } catch (error) {
                this.saving = false
                this.errors = error.response.data.errors || []
            }
        },

        handleSlideClick(dataset) {
            console.log(dataset.index)
        },

        cancel() {
            this.$nextTick(() => {
                this.$v.$reset()
            })
            this.mode = null
            this.slide = {
                image: null,
            }
        },

        async deleteSlide(slide) {
            const option = {
                text: '정말 삭제할까요?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e02424',
                confirmButtonText: '삭제합니다',
                cancelButtonText: '취소',
            }

            const result = await this.$swal(option)

            if (!result.value) {
                return
            }

            try {
                const response = await axios.delete(`/admin/slides/${slide.id}`)
                const idx = this.slides.findIndex(x => x.id === slide.id)
                this.fetchData()
                this.toast('슬라이드를 삭제했습니다.')
            } catch (error) {
                this.toast(error.response.data.message, 'error', 5000)
            }
        },

        toast(message, type, timer) {
            this.$swal({
                text: message,
                icon: type || 'success',
                confirmButtonText: '확인',
                timer: timer || 3000,
            })
        },
    },
}
</script>
